import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { supabase } from "../supabaseClient";
import { AuthThumb } from "../components/AuthPhoto";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const COLORS = ["#4a90d9", "#e0724a", "#3fae7a", "#9b6cd6", "#d9a93f", "#d94a6b"];

async function getAuthHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return {};
  return { Authorization: `Bearer ${session.access_token}` };
}

const pad = (n) => String(n).padStart(2, "0");
const dayKey = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`; // m is 1-12

// Which calendar day a photo belongs on:
// - taken_at (from the photo or the upload form) is stored as local time in UTC form,
//   so its date part is read directly
// - otherwise fall back to uploaded_at, which is real UTC, so convert it to local time
function photoDay(photo) {
  if (photo.taken_at) return photo.taken_at.slice(0, 10);
  if (!photo.uploaded_at) return "";
  const d = new Date(photo.uploaded_at);
  return dayKey(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

function todayKey() {
  const t = new Date();
  return dayKey(t.getFullYear(), t.getMonth() + 1, t.getDate());
}

function formatLong(key) {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
}

// Full-size photo for the lightbox
function FullPhoto({ filename, alt }) {
  const [src, setSrc] = useState(null);
  const urlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setSrc(null);
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await fetch(`/api/photos/${filename}`, { headers });
        if (!res.ok) return;
        const url = URL.createObjectURL(await res.blob());
        if (!cancelled) { urlRef.current = url; setSrc(url); }
      } catch (err) { console.error("Photo load error:", err); }
    })();
    return () => {
      cancelled = true;
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, [filename]);

  if (!src) return <div className="cal-lightbox-loading">Loading photo…</div>;
  return <img src={src} alt={alt} className="cal-lightbox-img" />;
}

export default function Calendar({ role }) {
  const isAdmin = role === "admin";
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-12
  const [photos, setPhotos] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDay, setSelectedDay] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const panelRef = useRef(null);

  // Event form
  const emptyForm = { id: null, title: "", description: "", end_date: "", color: COLORS[0] };
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const fetchMonth = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/calendar?year=${year}&month=${month}`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Server returned ${res.status}`);
      setPhotos(data.photos || []);
      setEvents(data.events || []);
    } catch (err) {
      setError(`Couldn't load this month: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchMonth(); }, [fetchMonth]);

  // Group photos and events by day
  const photosByDay = useMemo(() => {
    const map = {};
    for (const p of photos) {
      const k = photoDay(p);
      (map[k] = map[k] || []).push(p);
    }
    return map;
  }, [photos]);

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const start = ev.event_date;
      const end = ev.end_date || ev.event_date;
      const [sy, sm, sd] = start.split("-").map(Number);
      const cur = new Date(Date.UTC(sy, sm - 1, sd));
      // Walk each day of a multi-day event (capped for safety)
      for (let i = 0; i < 366; i++) {
        const k = cur.toISOString().slice(0, 10);
        if (k > end) break;
        (map[k] = map[k] || []).push(ev);
        cur.setUTCDate(cur.getUTCDate() + 1);
      }
    }
    return map;
  }, [events]);

  // Build grid cells
  const cells = useMemo(() => {
    const firstWeekday = new Date(year, month - 1, 1).getDay();
    const daysInMonth = new Date(year, month, 0).getDate();
    const list = [];
    for (let i = 0; i < firstWeekday; i++) list.push(null);
    for (let d = 1; d <= daysInMonth; d++) list.push(dayKey(year, month, d));
    while (list.length % 7 !== 0) list.push(null);
    return list;
  }, [year, month]);

  const dayPhotos = selectedDay ? photosByDay[selectedDay] || [] : [];
  const dayEvents = selectedDay ? eventsByDay[selectedDay] || [] : [];

  function changeMonth(delta) {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y--; }
    if (m > 12) { m = 1; y++; }
    setMonth(m); setYear(y); setSelectedDay(null); setShowForm(false);
  }

  function goToday() {
    const t = new Date();
    setYear(t.getFullYear()); setMonth(t.getMonth() + 1);
    setSelectedDay(todayKey()); setShowForm(false);
  }

  function selectDay(k) {
    const hasPhotos = (photosByDay[k] || []).length > 0;
    setShowForm(false);
    setFormError("");

    if (hasPhotos) {
      // Tile with photos: open the first photo in the lightbox
      setSelectedDay(k);
      setLightboxIndex(0);
    } else {
      setSelectedDay(k === selectedDay ? null : k);
    }
  }

  // Scroll the day panel into view when a day is picked (and after closing the lightbox)
  useEffect(() => {
    if (!selectedDay || lightboxIndex !== null || !panelRef.current) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    panelRef.current.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  }, [selectedDay, lightboxIndex]);

  // ── Lightbox keyboard nav ──
  const closeLightbox = () => setLightboxIndex(null);
  const goNext = useCallback(() =>
    setLightboxIndex((i) => (i === null ? i : (i + 1) % dayPhotos.length)), [dayPhotos.length]);
  const goPrev = useCallback(() =>
    setLightboxIndex((i) => (i === null ? i : (i - 1 + dayPhotos.length) % dayPhotos.length)), [dayPhotos.length]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIndex, goNext, goPrev]);

  // ── Event CRUD ──
  function startNewEvent() {
    setForm(emptyForm); setFormError(""); setShowForm(true);
  }

  function startEdit(ev) {
    setForm({
      id: ev.id, title: ev.title, description: ev.description || "",
      end_date: ev.end_date || "", color: ev.color || COLORS[0],
    });
    setFormError(""); setShowForm(true);
  }

  async function saveEvent(e) {
    e.preventDefault();
    setSaving(true); setFormError("");
    try {
      const headers = { ...(await getAuthHeaders()), "Content-Type": "application/json" };
      const original = form.id ? events.find((x) => x.id === form.id) : null;
      const body = {
        title: form.title,
        description: form.description,
        event_date: original ? original.event_date : selectedDay,
        end_date: form.end_date || null,
        color: form.color,
      };
      const res = await fetch(
        form.id ? `/api/calendar/events/${form.id}` : "/api/calendar/events",
        { method: form.id ? "PUT" : "POST", headers, body: JSON.stringify(body) }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Server returned ${res.status}`);
      setShowForm(false);
      fetchMonth();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function deleteEvent(ev) {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/calendar/events/${ev.id}`, { method: "DELETE", headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Server returned ${res.status}`);
      fetchMonth();
    } catch (err) {
      alert(`Couldn't delete event: ${err.message}`);
    }
  }

  const today = todayKey();
  const monthPrefix = `${year}-${pad(month)}-`;
  const monthPhotoCount = photos.filter((p) => photoDay(p).startsWith(monthPrefix)).length;

  return (
    <div className="cal-page">
      <header className="cal-header">
        <div>
          <h1 className="cal-title">{MONTHS[month - 1]} <span className="cal-year">{year}</span></h1>
          <p className="cal-sub">
            {loading ? "Loading…" : `${monthPhotoCount} photo${monthPhotoCount === 1 ? "" : "s"}, ${events.length} event${events.length === 1 ? "" : "s"}`}
          </p>
        </div>
        <div className="cal-nav">
          <button onClick={() => changeMonth(-1)} aria-label="Previous month">‹</button>
          <button onClick={goToday} className="cal-today-btn">Today</button>
          <button onClick={() => changeMonth(1)} aria-label="Next month">›</button>
        </div>
      </header>

      {error && <div className="cal-error">{error} <button onClick={fetchMonth}>Try again</button></div>}

      <div className="cal-grid" role="grid">
        {WEEKDAYS.map((w) => <div key={w} className="cal-weekday">{w}</div>)}
        {cells.map((k, i) => {
          if (!k) return <div key={`blank-${i}`} className="cal-cell cal-cell-blank" />;
          const dp = photosByDay[k] || [];
          const de = eventsByDay[k] || [];
          const cover = dp[0];
          const classes = ["cal-cell"];
          if (cover) classes.push("has-photo");
          if (k === today) classes.push("is-today");
          if (k === selectedDay) classes.push("is-selected");
          return (
            <button
              key={k}
              className={classes.join(" ")}
              onClick={() => selectDay(k)}
              aria-label={`${formatLong(k)}: ${dp.length} photos, ${de.length} events`}
            >
              {cover && (
                <AuthThumb filename={cover.file_path} alt="" className="cal-cell-cover" />
              )}
              <span className="cal-daynum">{Number(k.slice(8))}</span>
              {dp.length > 1 && <span className="cal-count">{dp.length}</span>}
              {de.length > 0 && (
                <span className="cal-events">
                  {de.slice(0, 2).map((ev) => (
                    <span key={ev.id} className="cal-event-chip" style={{ background: ev.color }}>
                      {ev.title}
                    </span>
                  ))}
                  {de.length > 2 && <span className="cal-event-more">+{de.length - 2}</span>}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {selectedDay && (
        <section className="cal-day-panel" ref={panelRef}>
          <div className="cal-day-head">
            <h2>{formatLong(selectedDay)}</h2>
            {isAdmin && !showForm && (
              <button className="cal-add-btn" onClick={startNewEvent}>Add event</button>
            )}
          </div>

          {dayEvents.length > 0 && (
            <ul className="cal-event-list">
              {dayEvents.map((ev) => (
                <li key={ev.id} style={{ borderLeftColor: ev.color }}>
                  <div>
                    <strong>{ev.title}</strong>
                    {ev.end_date && ev.end_date !== ev.event_date && (
                      <span className="cal-event-range">
                        {" "}({ev.event_date} to {ev.end_date})
                      </span>
                    )}
                    {ev.description && <p>{ev.description}</p>}
                  </div>
                  {isAdmin && (
                    <div className="cal-event-actions">
                      <button onClick={() => startEdit(ev)}>Edit</button>
                      <button onClick={() => deleteEvent(ev)}>Delete</button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          {showForm && (
            <form className="cal-form" onSubmit={saveEvent}>
              <input
                type="text" placeholder="Event name" required maxLength={120}
                value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
              <textarea
                placeholder="Notes (optional)" rows={2}
                value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
              <label>
                Ends on (optional)
                <input
                  type="date" min={selectedDay}
                  value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                />
              </label>
              <div className="cal-colors" role="radiogroup" aria-label="Event color">
                {COLORS.map((c) => (
                  <button
                    type="button" key={c} role="radio" aria-checked={form.color === c}
                    className={form.color === c ? "active" : ""} style={{ background: c }}
                    onClick={() => setForm({ ...form, color: c })} aria-label={c}
                  />
                ))}
              </div>
              {formError && <div className="cal-error">{formError}</div>}
              <div className="cal-form-actions">
                <button type="submit" disabled={saving}>
                  {saving ? "Saving…" : form.id ? "Save changes" : "Add event"}
                </button>
                <button type="button" onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </form>
          )}

          {dayPhotos.length > 0 ? (
            <div className="cal-photo-grid">
              {dayPhotos.map((p, i) => (
                <AuthThumb
                  key={p.id}
                  filename={p.file_path}
                  alt={p.filename || ""}
                  className="cal-photo"
                  onClick={() => setLightboxIndex(i)}
                />
              ))}
            </div>
          ) : (
            dayEvents.length === 0 && !showForm && (
              <p className="cal-empty">No photos or events on this day.</p>
            )
          )}
        </section>
      )}

      {lightboxIndex !== null && dayPhotos[lightboxIndex] && (
        <div className="cal-lightbox" onClick={closeLightbox}>
          <div className="cal-lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <FullPhoto filename={dayPhotos[lightboxIndex].file_path} alt={dayPhotos[lightboxIndex].filename || ""} />
            <div className="cal-lightbox-bar">
              <button onClick={goPrev} aria-label="Previous photo">‹</button>
              <span>{formatLong(selectedDay)}, photo {lightboxIndex + 1} of {dayPhotos.length}</span>
              <button onClick={goNext} aria-label="Next photo">›</button>
              <button onClick={closeLightbox} aria-label="Close">✕</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
